import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import type { ReactNode } from 'react';

export type HeroProps = {
  /** The page's `<h1>`. */
  title: ReactNode;
  /**
   * Optional supporting line under the heading. Hidden on mobile, where the
   * layout is space-constrained and the heading carries the page on its own;
   * it reappears centered on tablet+.
   *
   * Pages with nothing worth saying twice simply omit it — the upcoming-events
   * list, for instance, whose filter bar sits directly beneath the heading.
   */
  subtitle?: ReactNode;
  /**
   * Shown in the subtitle's place rather than alongside it: what a company's
   * guests scored it says more under the heading than copy every storefront
   * carries.
   *
   * Unlike the subtitle it shows at every width — it is one short line, and the
   * reason the subtitle goes on mobile is that it is not worth the space.
   */
  badge?: ReactNode;
};

/**
 * Centred page header: a heading, with an optional subtitle or badge beneath.
 *
 * Shared so every page that opens with a centred title looks the same — the
 * home page's "Discover your options" and the upcoming-events list both use it,
 * supplying their own copy.
 */
export function Hero({ title, subtitle, badge }: HeroProps) {
  return (
    <Stack
      as="header"
      gap="small"
      align={{ base: 'center', tablet: 'center' }}
      className={css({
        textAlign: 'center',
        // 16px top/bottom on mobile per the Figma; a bit more breathing
        // room on tablet+ where the page chrome is wider.
        paddingBlock: { base: '[16px]', tablet: 'medium' },
      })}
    >
      <Text as="h1" style="display">
        {title}
      </Text>
      {badge ??
        (subtitle ? (
          <Text
            as="p"
            style="body-large-regular"
            color="secondary"
            className={subtitleStyles}
          >
            {subtitle}
          </Text>
        ) : null)}
    </Stack>
  );
}

const subtitleStyles = css({
  display: { base: 'none', tablet: 'block' },
});
